import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL + '/api/v1';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add a request interceptor to include the token in headers
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

export const login = async (data: { email: string; password: string }) => {
    const response = await api.post('/auth/login', data);
    return response.data;
};

export const forgotPassword = async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
};

export const resetPassword = async (data: { token: string; password: string }) => {
    const response = await api.post('/auth/reset-password', data);
    return response.data;
};

export const checkOnboardingStatus = async () => {
    const response = await api.get('/members/onboarding-status');
    return response.data;
};

export const getMembers = async (search?: string) => {
    const params = search ? { search } : {};
    const response = await api.get('/members', { params });
    return response.data;
};

export const getCurrentMember = async () => {
    const response = await api.get('/members/me');
    return response.data;
};

export const getMemberById = async (id: string) => {
    const response = await api.get(`/members/${id}`);
    return response.data;
};

export const completeOnboarding = async (data: {
    picture?: string;
    currentPosition?: string;
    linkedinUrl?: string;
    city?: string;
    country?: string;
    bio?: string;
    phoneNumber?: string; // Nouveau champ
    acceptedRegulations: boolean;
    contributionAmount?: number;
}) => {
    const response = await api.post('/members/complete-onboarding', data);
    return response.data;
};

export const updateProfile = async (data: {
    currentPosition?: string;
    linkedinUrl?: string;
    city?: string;
    country?: string;
    bio?: string;
    phoneNumber?: string;
}) => {
    const response = await api.put('/members/me', data);
    return response.data;
};

export const uploadProfilePicture = async (file: File) => {
    const formData = new FormData();
    formData.append('picture', file);

    const response = await api.post('/members/upload-picture', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

    return response.data;
};

export const getMembershipRequestCounts = async () => {
    const response = await api.get('/membership-requests/counts');
    return response.data;
};

export const getMembershipRequests = async (status?: string) => {
    const response = await api.get('/membership-requests', {
        params: { status }
    });
    return response.data;
};

export const submitMembershipRequest = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    graduationYear: number;
    linkedinProfile?: string;
    role: 'ALUMNI' | 'STUDENT';
}) => {
    const response = await api.post('/membership-requests', data);
    return response.data;
};

export const finalizeMembership = async (data: {
    token: string;
    password: string;
}) => {
    const response = await api.put(`membership-requests/${data.token}/finalize`, data);
    return response.data;
};

export const acceptMembershipRequest = async (id: string) => {
    const response = await api.put(`/membership-requests/${id}/accept`);
    return response.data;
};

export const rejectMembershipRequest = async (id: string) => {
    const response = await api.put(`/membership-requests/${id}/reject`);
    return response.data;
};

export const sendMembershipReminder = async (id: string) => {
    const response = await api.put(`/membership-requests/${id}/send-reminder`);
    return response.data;
};

export const createDonation = async (data: any) => {
    const response = await api.post('/donations', data);
    return response.data;
};

export const getDonationStats = async () => {
    const response = await api.get('/donations/stats');
    return response.data;
};

export const getDonations = async (status?: string) => {
    const params = status ? { status } : {};
    const response = await api.get('/donations', { params });
    return response.data;
};

export const updateDonationStatus = async (id: string, status: string) => {
    const response = await api.patch(`/donations/${id}/status`, { status });
    return response.data;
};

export const getMyDonations = async () => {
    const response = await api.get('/donations/my');
    return response.data;
};

export const getAnalytics = async () => {
    const response = await api.get('/admin/analytics');
    return response.data;
};

export const logActivity = async (event: string, params?: Record<string, any>) => {
    await api.post('/admin/activity-logs', { event, params });
};

export const getActivityLogs = async (limit = 50, event?: string) => {
    const params: Record<string, any> = { limit };
    if (event) params.event = event;
    const response = await api.get('/admin/activity-logs', { params });
    return response.data;
};

// ── Events ──────────────────────────────────────────────────────────────────

export const getEvents = async () => {
    const response = await api.get('/events');
    return response.data;
};

export const getEvent = async (id: string) => {
    const response = await api.get(`/events/${id}`);
    return response.data;
};

export const createEvent = async (data: Record<string, any>) => {
    const response = await api.post('/events', data);
    return response.data;
};

export const uploadEventCover = async (file: File) => {
    const formData = new FormData();
    formData.append('coverImage', file);
    const response = await api.post('/events/upload-cover', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const updateEvent = async (id: string, data: Record<string, any>) => {
    const response = await api.put(`/events/${id}`, data);
    return response.data;
};

export const deleteEvent = async (id: string) => {
    const response = await api.delete(`/events/${id}`);
    return response.data;
};

export const registerToEvent = async (id: string) => {
    const response = await api.post(`/events/${id}/register`);
    return response.data;
};

export const unregisterFromEvent = async (id: string) => {
    const response = await api.delete(`/events/${id}/register`);
    return response.data;
};

export default api;
